// Single source of truth for sitemap structure AND breadcrumb trails.
// Adding a new service = add one entry to SERVICE_HUBS below.

import { EXPANSION_MARKETS, MARKETS, hasFeature } from './markets'
import { PT_TOPICS } from '../data/markets/registry'

export const SITE_URL = 'https://www.stringssg.com'

// Routes that must never appear in a sitemap.
// Mirrors (and replaces) the `exclude` list previously in next-sitemap.config.js.
// Patterns ending in * match by prefix.
export const EXCLUDED_ROUTES = [
    '/home*',
    '/history*',
    '/account*',
    '/notification*',
    '/services*',
    '/stripe-account*',
    '/user-activate',
    '/google-index',
    '/jobs*',
    '/rewards*',
    '/unsubscribe',        // email Unsubscribe link, noindex
    '/corporate',          // noindexed pages: a sitemap must not list them
'/corporate/programs',
    '/corporate/my-sessions',
    '/corporate/signup*',
    '/sg/ai-chat',
    '/faq/aircon-service-stringssg', // canonicalised to the aircon hub
    '/sitemap.xml',
    '/sitemaps*',
    '/team-job*',
    '/booking',
    '/profile*',       // service-provider profiles - intentionally excluded
    '/shared-job*',
    '/api/*',
    '/signin',
    '/signup',
    '/forgot-password',
    '/reset-password',
    '/service-provider-signup',
    '/app',                // app download bounce page (noindex)
    '/sg/blogs/local',     // emptied by utils/removed-wp-posts.js (noindex)
    '/404',
    '/500',
    '/_app',
    '/_document',
    '/_error',
]

// Every hub's trail starts at its country page (/sg, /ae, /my). "/" is the
// global brand page, not Singapore's home, so SG trails no longer start there.
const SINGAPORE = { name: 'Singapore', path: '/sg' }

// Ordered exactly as requested: main, aircon, personal trainer, handyman,
// domestic helper, then the rest. This order drives the <sitemapindex>.
export const SERVICE_HUBS = [
    {
        key: 'aircon-servicing-singapore',
        path: '/sg/aircon-servicing-singapore',
        title: 'Aircon Servicing Singapore',
        parent: SINGAPORE,
        // Mirrors pages/sg/aircon-servicing-singapore/[slug].js, which renders
        // 100 new + 100 legacy "hvac-services" posts. Without wpIncludeLegacy
        // the pre-2023 posts were missing from the sitemap entirely.
        wpCategory: 'hvac-services',
        wpIncludeLegacy: true,
        wpLimit: 100,
        blogPaths: ['/sg/blogs/hvac-services'],
        localityPattern: '/sg/aircon-servicing-singapore/locality/{locality}',
        // Built and reachable, but noindex and out of the sitemap: no job stats
        // yet (`ready: false` in data/aircon-locality-stats.js), so the page is
        // the template with the area name swapped in.
        localityNoindex: ['Sentosa'],
    },
    {
        key: 'personal-trainer',
        path: '/sg/personal-trainer',
        title: 'Personal Trainer Singapore',
        parent: SINGAPORE,
        wpCategory: 'personal-trainer-sg',
        blogPaths: ['/sg/blogs/health-fitness'],
        localityPattern: '/sg/personal-trainer/trainer-list/{locality}',
        // Each lists the nearest trainers within 10 km, so these show nearly the
        // same list as a neighbour that stays indexed: Rochor/Little India are
        // 0.19 km apart, Tanglin/Orchard 1.45 km, Tanjong Pagar/Raffles Place 1.33 km.
        localityNoindex: ['Rochor', 'Tanglin', 'Tanjong Pagar'],
        // Trainer profile pages. Only directly-employed trainers with a
        // substantial bio are listed - see getProfileRoutes in lib/sitemap.
        // Freelance/unverified profiles and near-empty bios stay out of the
        // sitemap; they remain reachable via the trainer-list pages.
        profilePattern: '/profile/{handleName}',
        profileCategory: 'Fitness Trainer',
        profileMinBioChars: 400,
    },
    {
        key: 'handyman-singapore-services',
        path: '/sg/handyman-singapore-services',
        title: 'Handyman Services Singapore',
        parent: SINGAPORE,
        // Mirrors pages/sg/handyman-singapore-services/[slug].js, which renders
        // 100 new + 100 legacy "repair-renovation" posts. Listing more than it
        // renders would put 404s in the sitemap.
        wpCategory: 'repair-renovation',
        wpIncludeLegacy: true,
        wpLimit: 100,
        blogPaths: ['/sg/blogs/repair-renovation'],
        localityPattern: null,
    },
    {
        key: 'part-time-cleaner-singapore',
        path: '/sg/part-time-cleaner-singapore',
        title: 'Part Time Cleaner Singapore',
        parent: SINGAPORE,
        // Mirrors pages/sg/part-time-cleaner-singapore/[slug].js, which renders
        // 100 new + 100 legacy "house-keeping" posts. This was null, so none of
        // those posts were in the sitemap.
        wpCategory: 'house-keeping',
        wpIncludeLegacy: true,
        wpLimit: 100,
        blogPaths: ['/sg/blogs/house-keeping'],
        localityPattern: null,
    },
    {
        key: 'electrician-services-singapore',
        path: '/sg/electrician-services-singapore',
        title: 'Electrician Services Singapore',
        parent: SINGAPORE,
        wpCategory: null,
        blogPaths: [],
        localityPattern: null,
    },
    {
        key: 'plumber-singapore-services',
        path: '/sg/plumber-singapore-services',
        title: 'Plumber Services Singapore',
        parent: SINGAPORE,
        wpCategory: null,
        blogPaths: [],
        localityPattern: null,
    },
    {
        key: 'property-agent',
        path: '/sg/property-agent',
        title: 'Property Agent Singapore',
        parent: SINGAPORE,
        wpCategory: 'housing',
        wpIncludeLegacy: true,
        blogPaths: ['/sg/blogs/housing'],
        localityPattern: null,
    },
    {
        key: 'financial-adviser-singapore',
        path: '/sg/financial-adviser-singapore',
        title: 'Financial Adviser Singapore',
        parent: SINGAPORE,
        wpCategory: null,
        blogPaths: ['/sg/blogs/finance-insurance-investment'],
        localityPattern: null,
    },

    // UAE and Malaysia (launched Oct 2026). `countryCd` + `city` make
    // getLocalityRoutes read that city's localities from utils/markets.js
    // instead of Singapore's, and `parent` puts the country page into the
    // breadcrumb trail. Titles use each market's own term - "AC" in the UAE,
    // "Aircond" in Malaysia - never Singapore's "Aircon".
    //
    // The personal trainer hubs deliberately have NO localityPattern and NO
    // profilePattern: there are no trainers in these cities yet, so there are
    // no trainer-list pages and no profiles to list.
    //
    // wpCategory stays null on these hubs: their WordPress posts are per
    // country, not per city, and live at /ae/blogs/<slug> and /my/blogs/<slug>
    // (categories in `blogWpCategories`, utils/markets.js). They reach the
    // sitemap through MAIN_WP_SOURCES below; /ae/blogs and /my/blogs are main
    // pages, outside every hub path.
    {
        key: 'ac-service-dubai',
        path: '/ae/ac-service-dubai',
        title: 'AC Service Dubai',
        countryCd: 'ae',
        city: 'dubai',
        parent: { name: 'UAE', path: '/ae' },
        wpCategory: null,
        blogPaths: [],
        localityPattern: '/ae/ac-service-dubai/locality/{locality}',
    },
    {
        key: 'ac-service-abu-dhabi',
        path: '/ae/ac-service-abu-dhabi',
        title: 'AC Service Abu Dhabi',
        countryCd: 'ae',
        city: 'abu-dhabi',
        parent: { name: 'UAE', path: '/ae' },
        wpCategory: null,
        blogPaths: [],
        localityPattern: '/ae/ac-service-abu-dhabi/locality/{locality}',
    },
    {
        key: 'aircond-service-kuala-lumpur',
        path: '/my/aircond-service-kuala-lumpur',
        title: 'Aircond Service Kuala Lumpur',
        countryCd: 'my',
        city: 'kuala-lumpur',
        parent: { name: 'Malaysia', path: '/my' },
        wpCategory: null,
        blogPaths: [],
        localityPattern: '/my/aircond-service-kuala-lumpur/locality/{locality}',
    },
    {
        key: 'personal-trainer-dubai',
        path: '/ae/personal-trainer-dubai',
        title: 'Personal Trainer Dubai',
        countryCd: 'ae',
        city: 'dubai',
        parent: { name: 'UAE', path: '/ae' },
        wpCategory: null,
        blogPaths: [],
        localityPattern: null,
    },
    {
        key: 'personal-trainer-abu-dhabi',
        path: '/ae/personal-trainer-abu-dhabi',
        title: 'Personal Trainer Abu Dhabi',
        countryCd: 'ae',
        city: 'abu-dhabi',
        parent: { name: 'UAE', path: '/ae' },
        wpCategory: null,
        blogPaths: [],
        localityPattern: null,
    },
    {
        key: 'personal-trainer-kuala-lumpur',
        path: '/my/personal-trainer-kuala-lumpur',
        title: 'Personal Trainer Kuala Lumpur',
        countryCd: 'my',
        city: 'kuala-lumpur',
        parent: { name: 'Malaysia', path: '/my' },
        wpCategory: null,
        blogPaths: [],
        localityPattern: null,
    },
]

// Priority / changefreq per depth tier.
export const TIERS = {
    home:     { priority: '1.0', changefreq: 'daily' },
    country:  { priority: '0.9', changefreq: 'weekly' }, // /sg, /ae, /my
    hub:      { priority: '1.0', changefreq: 'daily' },
    child:    { priority: '0.8', changefreq: 'weekly' },
    blog:     { priority: '0.6', changefreq: 'weekly' },
    locality: { priority: '0.5', changefreq: 'monthly' },
    profile:  { priority: '0.4', changefreq: 'monthly' },
    main:     { priority: '0.7', changefreq: 'monthly' },
}

// WordPress-backed dynamic routes that don't belong to a service hub.
// Mirrors pages/blogs/[slug].js (category "general").
// The UAE / Malaysia entries mirror pages/ae|my/blogs/[slug].js and are empty
// until a market lists categories in `blogWpCategories`.
export const MAIN_WP_SOURCES = [
    { basePath: '/blogs', wpCategory: 'general', includeLegacy: true },
    ...EXPANSION_MARKETS.flatMap((cd) =>
        (MARKETS[cd].blogWpCategories ?? []).map(({ wpCategory }) => ({ basePath: `/${cd}/blogs`, wpCategory }))
    ),
]

export const getHubByKey = (key) => SERVICE_HUBS.find((h) => h.key === key)

// Longest-path match so nested hubs resolve correctly.
export const getHubForPath = (path) =>
    SERVICE_HUBS
        .filter((h) => path === h.path || path.startsWith(h.path + '/'))
        .sort((a, b) => b.path.length - a.path.length)[0] ?? null

// UAE / Malaysia personal-trainer topic pages (female, freelance, seniors,
// price...) describe a local trainer roster. Until a country has one
// (`trainerRoster` in utils/markets.js) they are noindex - PtTopicPage reads
// the same switch - and stay out of the sitemap. The city PT hubs and the
// guides are not affected.
export const NOINDEX_PT_TOPIC_ROUTES = Object.entries(PT_TOPICS).flatMap(([key, topics]) => {
    const [countryCd, citySlug] = key.split('/')
    const market = MARKETS[countryCd]
    const hub = market?.hubs?.pt?.[citySlug]
    if (!hub || hasFeature(market, 'trainerRoster')) return []
    return topics.map((topic) => `${hub}/${topic.slug}`)
})

export const isExcluded = (route) =>
    NOINDEX_PT_TOPIC_ROUTES.includes(route) ||
    EXCLUDED_ROUTES.some((pattern) =>
        pattern.endsWith('*')
            ? route.startsWith(pattern.slice(0, -1))
            : route === pattern
    )

// Shared by the locality pages' robots tag and lib/sitemap, so a locality is
// noindexed exactly when it is left out of the sitemap.
export const isNoindexLocality = (hubKey, locality) =>
    (SERVICE_HUBS.find((hub) => hub.key === hubKey)?.localityNoindex ?? []).includes(locality)
